import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { queryClient, type RouterInputs, trpc, type trpcQueryUtils } from '@client/trpc/client';
import {
    CLAIM_RECORD_PREFETCH_OPTIONS,
    CLAIM_RECORD_QUERY_OPTIONS,
    DETAIL_RECORD_PREFETCH_OPTIONS,
} from '@client/trpc/query-options';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { ORG_ROLES } from '@shared/constants';
import { skipToken } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { shouldPrefetchDetailRouteData } from '../../detail-route-prefetch';

/**
 * `claim.getClaim` input for the claim details page. The route's `beforeLoad` prefetch and
 * the ClaimDetails observer must serialise to the same query key, or the prefetch warms an
 * entry nothing reads — build it here instead of re-typing the literal.
 */
export function claimRecordInput(id: bigint): RouterInputs['claim']['getClaim'] {
    return { id };
}

/**
 * Gate for the claim details routes' `beforeLoad` warm-ups (the details route and its tabs).
 * Mirrors the `/console/$orgId/claims` layout (roles and the claim module) and requires a
 * token outside the refresh window, so a signed-out deep link sends no anonymous request.
 */
export function isClaimDetailPrefetchAuthorised(routeOrgId: bigint): boolean {
    const authState = useAuthStore.getState();
    const user = selectUser(authState);
    return shouldPrefetchDetailRouteData({
        hasValidToken: authState.hasValidToken({
            minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
        }),
        userRoles: user?.roles,
        allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
        hydratedOrg: selectOrg(authState),
        routeOrgId,
        requiredModule: 'claim',
    });
}

/** The ClaimDetails observer of the claim record. */
export function useClaimRecordQuery(id: bigint | undefined) {
    return trpc.claim.getClaim.useQuery(id ? claimRecordInput(id) : skipToken, CLAIM_RECORD_QUERY_OPTIONS);
}

/**
 * Warm the claim record from the details route's `beforeLoad`, so `getClaim` starts while the
 * route chunks load instead of after ClaimDetails mounts.
 *
 * - Skipped while the record is already observed (ClaimDetails is mounted for this claim).
 *   `beforeLoad` reruns for the retained details match on every tab switch and on hover-intent
 *   preloads of in-page links; the mounted observer owns the record's freshness there, and a
 *   refetch would resend the full payload and re-render the details tree mid tab switch.
 *   Another claim, or a return to this one, is unobserved and still prefetches.
 * - A navigation uses the observer's freshness window, so a record the observer would refetch
 *   on mount is refreshed here and the observer joins the in-flight request.
 * - A hover-intent preload keeps the 30 s detail-record window: a cached record already paints
 *   instantly, and the click reruns `beforeLoad` as a navigation, which refreshes it.
 */
export function prefetchClaimRecord(
    trpcUtils: typeof trpcQueryUtils,
    id: bigint,
    { preload }: { preload: boolean },
): void {
    const input = claimRecordInput(id);
    const observerCount =
        queryClient
            .getQueryCache()
            .find({ queryKey: getQueryKey(trpc.claim.getClaim, input, 'query'), exact: true })
            ?.getObserversCount() ?? 0;
    if (observerCount > 0) {
        return;
    }
    void trpcUtils.claim.getClaim.prefetch(
        input,
        preload ? DETAIL_RECORD_PREFETCH_OPTIONS : CLAIM_RECORD_PREFETCH_OPTIONS,
    );
}
